<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStreamingMarkdown } from '@/composables/useStreamingMarkdown'
import { useStreamingTable } from '@/composables/useStreamingTable'

interface Props {
  content: string
  isStreaming?: boolean
  streamingSpeed?: number // Characters per chunk when simulating streaming
  streamingDelay?: number // Delay between chunks in milliseconds
  turboMode?: boolean // If true, disable animation for instant streaming
}

const props = withDefaults(defineProps<Props>(), {
  isStreaming: false,
  streamingSpeed: 15, // Even faster - 15 characters per chunk
  streamingDelay: 10, // Very fast - 10ms delay
  turboMode: false, // Default to animated streaming
})

const emit = defineEmits<{
  streamingComplete: []
}>()

const displayedContent = ref('')
const streamingIndex = ref(0)
const isInternallyStreaming = ref(false)

const { parseStreamingMarkdown } = useStreamingMarkdown()
const { parseStreamingTable, renderTableToHTML } = useStreamingTable()

// Parse content to handle tables separately
const processedContent = computed(() => {
  const content = displayedContent.value

  // Check if content contains a table
  const tableResult = parseStreamingTable(content)
  if (tableResult.isTable && tableResult.tableData) {
    const tableHTML = renderTableToHTML(tableResult.tableData, props.isStreaming)
    const beforeTable = content.substring(0, content.indexOf('|'))
    const afterTable = tableResult.remainingContent || ''

    const beforeTableParsed = beforeTable ? parseStreamingMarkdown(beforeTable) : ''
    const afterTableParsed = afterTable ? parseStreamingMarkdown(afterTable) : ''

    return beforeTableParsed + tableHTML + afterTableParsed
  }

  // No table, parse as regular markdown
  return parseStreamingMarkdown(content)
})

// Watch for content changes and handle streaming
watch(
  () => props.content,
  (newContent) => {
    if (!newContent) {
      displayedContent.value = ''
      streamingIndex.value = 0
      return
    }

    if (props.isStreaming && !props.turboMode) {
      // Streaming mode - animate the content changes
      if (newContent.length > displayedContent.value.length) {
        streamNextChunk(newContent)
      }
      else {
        displayedContent.value = newContent
        streamingIndex.value = newContent.length
      }
    }
    else {
      // Not streaming or turbo mode - show all content immediately
      displayedContent.value = newContent
      streamingIndex.value = newContent.length
    }
  },
  { immediate: true, flush: 'sync' },
)

function streamNextChunk(targetContent: string) {
  if (!isInternallyStreaming.value) {
    isInternallyStreaming.value = true
    requestAnimationFrame(() => streamContent(targetContent))
  }
}

function streamContent(targetContent: string) {
  if (streamingIndex.value >= targetContent.length) {
    isInternallyStreaming.value = false
    emit('streamingComplete')
    return
  }

  const nextIndex = Math.min(
    streamingIndex.value + props.streamingSpeed,
    targetContent.length,
  )

  displayedContent.value = targetContent.substring(0, nextIndex)
  streamingIndex.value = nextIndex

  // Continue streaming
  setTimeout(() => {
    requestAnimationFrame(() => streamContent(targetContent))
  }, props.streamingDelay) // Use configurable delay
}

// Method to append content (useful for real-time streaming)
function appendContent(newContent: string) {
  const updatedContent = displayedContent.value + newContent
  if (props.isStreaming) {
    streamNextChunk(updatedContent)
  }
  else {
    displayedContent.value = updatedContent
  }
}

// Expose methods for parent components
defineExpose({
  appendContent,
})
</script>

<template>
  <article class="streaming-message prose m-0 p-0">
    <div
      class="markdown-content"
      v-html="processedContent"
    />
  </article>
</template>

<style scoped>
.streaming-message {
  /* Add any specific styling for streaming messages */
}

.markdown-content {
  /* Ensure proper styling for markdown content */
}

/* Streaming table styles */
:deep(.streaming-table) {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
}

:deep(.streaming-table th),
:deep(.streaming-table td) {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

:deep(.streaming-table th) {
  background-color: #f5f5f5;
  font-weight: 600;
}

:deep(.streaming-table tr:nth-child(even)) {
  background-color: #f9f9f9;
}

/* Code block styling */
:deep(pre) {
  background-color: #f6f8fa;
  border-radius: 6px;
  padding: 16px;
  overflow-x: auto;
}

:deep(code) {
  background-color: #f6f8fa;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 0.9em;
}

:deep(pre code) {
  background-color: transparent;
  padding: 0;
}

/* Ensure proper spacing for lists and other elements */
:deep(ul), :deep(ol) {
  padding-left: 1.5rem;
}

:deep(blockquote) {
  border-left: 4px solid #ddd;
  margin: 1rem 0;
  padding-left: 1rem;
  color: #666;
}
</style>
